import { Portal, useDisclosure } from "@chakra-ui/react";
import React, { memo, useEffect, useRef } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  AppAvatar,
  AppContainer,
  BasicRoute,
  Col,
  Logo,
  Row,
  UiBox,
  UiButton,
  UiDivider,
  UiLink,
  UiPopover,
  UiPopoverContent,
  UiPopoverTrigger,
  UiText,
  UiVStack,
  useBreakpointValue,
} from "~/components/elements";
import { PAGE_URLS, PAGES } from "~/constants";
import { setMenuHeight } from "~/redux/ui/slice";
import { useAppDispatch, useAppSelector } from "~/hooks";
import { sharedStyle } from "~/theme/shared-style";
import { colors } from "~/utils/theme";
import { LogoutIcon, MagicWandIcon } from "~/components/icons/SvgIcons";
import isEmpty from "lodash/isEmpty";
import { generateGreetings } from "~/utils/helpers";
import { EDashboardStep, setDashboardProgress } from "~/redux/dashboards/slice";

interface Props {}

export const renderMenuItems = (item: BasicRoute, parentPath = "", isMobile = false, cb: any = undefined): any => {
  if (item.routes) {
    if (item.label) {
      // Mean has nested children
      return renderParentMenuItem(item, item.path, isMobile, cb);
    }
    // Mean this is a layout component
    return item.routes.map(route => renderMenuItems(route, parentPath, isMobile, cb));
  }
  if (item.label) {
    const fullPath = parentPath && parentPath !== "/" ? `${parentPath}${item.path}` : item.path;
    return <SingleMenuItem key={fullPath} item={item} parentPath={parentPath} isMobile={isMobile} cb={cb} />;
  }
  return null;
};

const renderParentMenuItem = (item: BasicRoute, parentPath = "", isMobile = false, cb: any = undefined) => (
  <UiBox key={`parent-menu-${item.path}`} _hover={{ cursor: "pointer" }}>
    <Row px={5} alignItems="flex-end">
      <UiLink _hover={{ color: "link.active" }}>{item.label}</UiLink>
    </Row>
  </UiBox>
);

function SingleMenuItem({
  item,
  parentPath = "",
  isMobile = false,
  cb = undefined,
}: {
  item: BasicRoute;
  parentPath: string;
  isMobile: boolean;
  cb: any;
}) {
  const { pathname } = useLocation();

  const fullPath = parentPath && parentPath !== "/" ? `${parentPath}${item.path}` : item.path;
  const isActive = pathname.includes(fullPath);

  if (fullPath !== item.path) {
    // Submenu
    return (
      <UiBox key={`menu-${fullPath}`} fontWeight="bold" my={2} _hover={{ cursor: "pointer" }}>
        <UiLink as={Link} to={fullPath} _hover={{ color: "link.active" }}>
          {item.label}
        </UiLink>
      </UiBox>
    );
  }
  if (isMobile) {
    return (
      <UiBox key={`menu-${fullPath}`} fontWeight="bold" _hover={{ cursor: "pointer" }}>
        <Row alignItems="flex-end" my={5}>
          <UiLink
            onClick={() => cb && cb()}
            as={Link}
            to={fullPath}
            fontSize="xl"
            _focus={{ boxShadow: "none" }}
            _hover={{ color: isActive ? undefined : colors.baliHai }}
            zIndex={2}
          >
            {item.label}
          </UiLink>
        </Row>
        {/* <UiDivider bordercolor="secondary.600" /> */}
      </UiBox>
    );
  }

  return (
    <UiBox key={`menu-${fullPath}`} fontWeight="bold" _hover={{ cursor: "pointer" }}>
      <Row mr="2.5em" alignItems="flex-start">
        <UiLink
          as={Link}
          _focus={{ boxShadow: "none" }}
          to={fullPath}
          _hover={{ color: isActive ? undefined : colors.baliHai }}
          fontSize="13px"
          fontWeight={600}
          zIndex={2}
        >
          {item.label}
        </UiLink>
      </Row>
    </UiBox>
  );
}

export const PrivateNavigationBar = memo((props: Props) => {
  const dispatch = useAppDispatch();
  // const { user: currentUser } = getCurrentUserFromStorage();
  const currentUser = useAppSelector(state => state.auth.currentUser);
  const jobData = useAppSelector(state => state.dashboard.jobData);
  const history = useHistory();
  const ref = useRef<HTMLDivElement>(null);
  const [isAtTop, setIsAtTop] = React.useState(true);
  const imgHeight = useBreakpointValue<any>({ base: "1.3rem", sm: "1.875rem" });

  // Add useDisclosure hook for popover control
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    dispatch(setMenuHeight({ height: ref?.current?.clientHeight! }));
    window.onscroll = () => {
      setIsAtTop(window.scrollY < 10);
    };
  }, [dispatch]);

  const handleViewResults = () => {
    if (!isEmpty(jobData)) {
      dispatch(setDashboardProgress({ step: EDashboardStep.Download, screenIndex: 0 }));
      history.push(PAGES.VIEW_RESULTS);
    }
    // Close popover after action
    onClose();
  };

  const handleLogout = () => {
    history.push(PAGE_URLS.LOGOUT);
    onClose();
    // Close popover after action
  };

  return isEmpty(currentUser) ? null : (
    <UiBox
      // className="sticky-header"
      // right={0}
      // height="84px"
      // top={0}
      // bg={!isAtTop ? "white" : "white"}
      // sx={{ transition: "all .3s ease-in" }}
      ref={ref}
      bg="#FFFFFF1A"
      borderBottom="1px"
      borderStyle="solid"
      borderColor="#0B0D2B1A"
      px={{ base: 4, md: 5 }}
    >
      <AppContainer as={Col} justifyContent="center" h="100%" maxW={sharedStyle.pageMaxWidth}>
        <Row alignItems="center" justifyContent="space-between" py={2.5}>
          <Logo height={imgHeight} textProps={{ fontSize: "xl" }} />
          <UiBox>
            <UiPopover placement="bottom-start" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
              {/* @ts-ignore */}
              <UiPopoverTrigger>
                <UiButton variant="unstyled" h="2.5rem">
                  <Row bg="white" rounded="full" alignItems="center" p="1.5">
                    <AppAvatar
                      src={currentUser?.profile_picture}
                      height="1.75rem"
                      width="1.75rem"
                      boxSizing="content-box"
                    />
                    <UiText
                      className={currentUser?.is_all_access_plan ? "gradient-text" : ""}
                      vr="body2"
                      minW="6rem"
                      textAlign="center"
                      mx={2}
                      noOfLines={1}
                      maxWidth={{ base: "8rem", sm: "none" }}
                      display="block"
                    >
                      {currentUser?.email || generateGreetings()}
                    </UiText>
                  </Row>
                </UiButton>
              </UiPopoverTrigger>
              <Portal>
                {/* <UiBox zIndex="popover" pos="relative"> */}
                <UiPopoverContent w="auto">
                  <UiBox rounded="xl" py={1} w="11.25rem">
                    <UiVStack alignItems="flex-start" px={0}>
                      {/* <UiButton */}
                      {/*  variant="unstyled" */}
                      {/*  h="2.5rem" */}
                      {/*  minH="0" */}
                      {/*  px={4} */}
                      {/*  onClick={() => { */}
                      {/*    history.push(PAGES.PROFILE); */}
                      {/*    onClose(); */}
                      {/*  }} */}
                      {/* > */}
                      {/*  <Row align="center"> */}
                      {/*    <AvatarIcon boxSize="1.5rem" /> */}
                      {/*    <UiText vr="body2" ml={2}> */}
                      {/*      My Profile */}
                      {/*    </UiText> */}
                      {/*  </Row> */}
                      {/* </UiButton> */}
                      <UiButton
                        variant="unstyled"
                        h="2.5rem"
                        minH="0"
                        px={4}
                        onClick={handleViewResults}
                        isDisabled={isEmpty(jobData)}
                      >
                        <Row align="center">
                          <MagicWandIcon boxSize="1.5rem" />
                          <UiText vr="body2" ml={2}>
                            My Results
                          </UiText>
                        </Row>
                      </UiButton>
                      <UiDivider borderColor="neutral.gray100" />
                      <UiButton variant="unstyled" h="2.5rem" minH="0" px={4} onClick={handleLogout}>
                        <Row align="center">
                          <LogoutIcon boxSize="1.5rem" />
                          <UiText vr="body2" ml={2}>
                            Log out
                          </UiText>
                        </Row>
                      </UiButton>
                    </UiVStack>
                  </UiBox>
                </UiPopoverContent>
                {/* </UiBox> */}
              </Portal>
            </UiPopover>
          </UiBox>
        </Row>
      </AppContainer>
    </UiBox>
  );
});
